import { Checkbox } from '@mui/material';
import { FormikProps, FormikProvider, useFormik } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

import { TestTask, testItemFormSchema } from 'shared/types/task';

interface Props {
  testTaskFormik: FormikProps<TestTask>;
}

export const TestItemForm = ({ testTaskFormik }: Props) => {
  const { t } = useTranslation();

  const formik = useFormik({
    initialValues: {
      text: '',
      isRight: false,
    },
    onSubmit: values => {
      const newId = uuidv4();
      const newTestItems = [...testTaskFormik.values.testItems, { ...values, id: newId }];
      testTaskFormik.setFieldValue('testItems', newTestItems);
      formik.resetForm();
    },
    validationSchema: testItemFormSchema,
  });

  return (
    <FormikProvider value={formik}>
      <div>
        <div className="relative !mb-2">
          <div className="text-[14px] font-bold uppercase lg:text-[18px]">{`${t('newOption')}:`}</div>

          <textarea
            value={formik.values.text}
            className={
              formik.errors.text
                ? 'h-[70px] w-full border-b-2 border-none bg-[linear-gradient(to_right,_#f5f5f5_1px,_transparent_1px),_linear-gradient(to_left,_#f5f5f5_1px,_transparent_1px),_repeating-linear-gradient(#f5f5f5,_#f5f5f5_33px,_#FA6238_35px,_#97A0C3_31px,_#f5f5f5_35px)] bg-local pt-[8px] text-[18px] font-medium leading-[35px] placeholder:text-lg lg:text-[22px] lg:placeholder:text-[22px]'
                : 'h-[70px] w-full border-b-2 border-none bg-[linear-gradient(to_right,_#f5f5f5_1px,_transparent_1px),_linear-gradient(to_left,_#f5f5f5_1px,_transparent_1px),_repeating-linear-gradient(#f5f5f5,_#f5f5f5_33px,_#242C2C_35px,_#97A0C3_31px,_#f5f5f5_35px)] bg-local pt-[8px] text-[18px] font-medium leading-[35px] placeholder:text-lg lg:text-[22px] lg:placeholder:text-[22px]'
            }
            onChange={event => formik.setFieldValue('text', event.target.value)}
            placeholder={t('enter option')}
          />

          {formik.errors.text && (
            <p className="absolute left-0 top-14 !mb-0 inline-block h-fit text-sm font-medium text-red-600 lg:top-[60px]">
              {t(formik.errors.text)}
            </p>
          )}
        </div>

        <div className="flex items-center gap-[10px]">
          <Checkbox
            id="isRight"
            name="isRight"
            checked={formik.values.isRight}
            sx={{
              padding: 0,
              color: '#08B781',
              '&.Mui-checked': {
                color: '#08B781',
              },
            }}
            onChange={() => formik.setFieldValue('isRight', !formik.values.isRight)}
          />

          <label htmlFor="isRight" className="text-base font-bold leading-[22px]">
            {t('mark as a right answer')}
          </label>
        </div>
      </div>

      <button
        type="button"
        onClick={formik.submitForm}
        disabled={!formik.isValid}
        className={`btn btn-primary moreStatic flex w-full grow items-center gap-[10px] !text-base font-bold lg:w-[232px] ${formik.errors.text && '!cursor-auto'}`}
      >
        <span className="icon icon-plus"></span>
        {t('add option')}
      </button>
    </FormikProvider>
  );
};
